"use client"

import { useState, useEffect, useCallback } from "react"
import { Group, Avatar, Menu, Text, Box } from "@forestsync/ui"
import { TbChevronDown, TbLogout, TbLayoutDashboard, TbWallet, TbLeaf } from "react-icons/tb"
import { useAuth } from "@/components/layout/AuthProvider"
import { clearIdentity } from "@/lib/analytics"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { getTraderFundingInfoAction } from "@/actions/trade-payment"
import { DepositFundsModal } from "@/components/trading/DepositFundsModal"

interface UserMenuProps {
  email: string
  logoUrl?: string | null
}

interface FundingInstructions {
  iban: string
  bic: string
  accountHolder: string
  reference: string
}

export function UserMenu({ email, logoUrl }: UserMenuProps) {
  const t = useTranslations("nav")
  const { signOut } = useAuth()
  const [balance, setBalance] = useState<number | null>(null)
  const [fundingInstructions, setFundingInstructions] = useState<FundingInstructions | null>(null)
  const [depositOpen, setDepositOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const fetchFundingInfo = useCallback(() => {
    setIsLoading(true)
    getTraderFundingInfoAction()
      .then((res) => {
        if ("balanceCents" in res) {
          setBalance(res.balanceCents / 100)
          setFundingInstructions(res.fundingInstructions)
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => { fetchFundingInfo() }, [fetchFundingInfo])

  const namePart = email.split("@")[0]
  const displayName = namePart
    .split(/[._-]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")

  const initials = displayName
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const handleSignOut = async () => {
    clearIdentity()
    await signOut()
  }

  return (
    <>
      <Menu shadow="md" width={220} position="bottom-end">
        <Menu.Target>
          <Group
            gap="xs"
            className="cursor-pointer px-2 py-1 rounded-lg fs-hover-surface transition-colors"
          >
            <Avatar size={32} radius="xl" color="green" src={logoUrl ?? undefined} imageProps={{ className: "object-contain" }}>
              {!logoUrl && initials}
            </Avatar>
            <Box visibleFrom="sm">
              <TbChevronDown size={14} />
            </Box>
          </Group>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label data-clarity-mask="true">
            {/* Truncate: a long address (e.g. Google's "x%domain@gtempaccount.com") overflowed the
                fixed-width menu past the window edge, and the menu's repositioning then looped. */}
            <Text size="sm" fw={500} truncate="end" title={displayName}>{displayName}</Text>
            <Text size="xs" c="dimmed" truncate="end" title={email ?? undefined}>{email}</Text>
          </Menu.Label>

          <Menu.Divider />

          <Menu.Item leftSection={<TbLayoutDashboard size={16} />} component={Link} href="/dashboard">
            {t("dashboard")}
          </Menu.Item>

          <Menu.Item leftSection={<TbLeaf size={16} />} component={Link} href="/dashboard/credits">
            {t("myCredits")}
          </Menu.Item>

          <Menu.Item
            leftSection={<TbWallet size={16} />}
            rightSection={
              balance !== null ? (
                <Text size="xs" c={balance > 0 ? "teal" : "orange"} fw={600}>
                  €{balance.toFixed(2)}
                </Text>
              ) : undefined
            }
            onClick={() => setDepositOpen(true)}
          >
            {t("wallet")}
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item color="red" leftSection={<TbLogout size={16} />} onClick={handleSignOut}>
            {t("signOut")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <DepositFundsModal
        opened={depositOpen}
        onClose={() => setDepositOpen(false)}
        fundingInstructions={fundingInstructions}
        onRetry={fetchFundingInfo}
        isRetrying={isLoading}
      />
    </>
  )
}
